
import React from 'react';
import Layout from '@/components/Layout/Layout';
import GrossMarginCalculator from '@/components/SpecialCalculators/FinancialCalculators/GrossMarginCalculator';
import { Link } from 'react-router-dom';

const GrossMarginCalculatorPage = () => {
  return (
    <Layout>
      <div className="max-w-5xl mx-auto px-4">
        {/* Header Section */}
        <div className="mb-8">
          <div className="text-sm text-gray-500 mb-2">
            <Link to="/" className="hover:underline">Home</Link> {' > '} 
            <Link to="/special" className="hover:underline">Special Tools</Link> {' > '} 
            <span>Gross Margin Calculator</span>
          </div>
          <h1 className="text-3xl font-bold mb-3">Gross Margin Calculator</h1>
          <p className="text-gray-700">
            Calculate your gross profit margin to understand how efficiently your business converts sales into profit after covering direct costs.
          </p>
        </div>

        {/* Calculator Section */}
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 mb-8">
          <GrossMarginCalculator />
        </div>

        {/* Information Section */}
        <div className="bg-[#E6F3FF] p-6 rounded-lg mb-8">
          <h2 className="text-2xl font-semibold mb-3">How to use the Gross Margin Calculator</h2>
          <ol className="list-decimal list-inside space-y-2 text-gray-700 ml-2">
            <li>Enter your total revenue or sales for the period</li>
            <li>Input your cost of goods sold (COGS) - direct costs to produce your products or services</li>
            <li>The calculator will automatically compute your gross profit and gross margin percentage</li>
            <li>Use the results to compare with industry standards and track performance over time</li>
            <li>Analyze trends to identify pricing or cost optimization opportunities</li>
          </ol>
        </div>

        {/* Additional Information */}
        <div className="mb-8">
          <h2 className="text-2xl font-semibold mb-3">Understanding Gross Margin</h2>
          <p className="text-gray-700 mb-3">
            Gross margin is a crucial financial metric that measures the percentage of revenue remaining after deducting 
            the cost of goods sold (COGS). It indicates how efficiently a company produces its goods or services and 
            represents the profit available to cover operating expenses and generate net income.
          </p>
          <p className="text-gray-700 mb-3">
            The gross margin formula is: Gross Margin = (Revenue - COGS) / Revenue × 100. A higher gross margin indicates 
            better pricing power, efficient production, or both. This metric is essential for pricing decisions, 
            cost management, and assessing business sustainability.
          </p>
          <h3 className="text-xl font-medium mb-2">Industry Benchmarks</h3>
          <ul className="list-disc list-inside space-y-2 text-gray-700 ml-2">
            <li><strong>Software/Technology:</strong> 70-90% - High margins due to low incremental costs</li>
            <li><strong>Retail:</strong> 20-50% - Varies by product category and business model</li>
            <li><strong>Manufacturing:</strong> 15-40% - Depends on industry and production efficiency</li>
            <li><strong>Food & Beverage:</strong> 25-40% - Brand positioning affects margins significantly</li>
            <li><strong>Healthcare:</strong> 40-80% - Regulated pricing and specialized services</li>
          </ul>
        </div>

        {/* Related Calculators */}
        <div className="mb-8">
          <h2 className="text-2xl font-semibold mb-3">Related Financial Calculators</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
            <Link to="/special/profit-margin" className="p-4 border border-gray-200 rounded-md hover:bg-gray-50 flex items-center">
              <span className="text-xl mr-2">📊</span> Profit Margin Calculator
            </Link>
            <Link to="/special/ebitda" className="p-4 border border-gray-200 rounded-md hover:bg-gray-50 flex items-center">
              <span className="text-xl mr-2">💹</span> EBITDA Calculator
            </Link>
            <Link to="/special/roi" className="p-4 border border-gray-200 rounded-md hover:bg-gray-50 flex items-center">
              <span className="text-xl mr-2">💰</span> ROI Calculator
            </Link>
            <Link to="/special/discount" className="p-4 border border-gray-200 rounded-md hover:bg-gray-50 flex items-center">
              <span className="text-xl mr-2">🏷️</span> Discount Calculator
            </Link>
            <Link to="/special/percentage" className="p-4 border border-gray-200 rounded-md hover:bg-gray-50 flex items-center">
              <span className="text-xl mr-2">%</span> Percentage Calculator
            </Link>
            <Link to="/special/investment" className="p-4 border border-gray-200 rounded-md hover:bg-gray-50 flex items-center">
              <span className="text-xl mr-2">📈</span> Investment Calculator
            </Link>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default GrossMarginCalculatorPage;
